/**
 * NavItem — Individual nav item with icon, label, active indicator, and badge.
 * Story 13.2
 */

import type { LucideIcon } from "lucide-react";
import { NavBadge } from "./NavBadge";

interface NavItemProps {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  isActive: boolean;
  isCollapsed: boolean;
  badgeCount?: number;
  onClick?: (path: string) => void;
}

export function NavItem({
  id,
  label,
  path,
  icon: Icon,
  isActive,
  isCollapsed,
  badgeCount,
  onClick,
}: NavItemProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onClick?.(path);
  };

  return (
    <li>
      <a
        href={path}
        onClick={handleClick}
        className={`
          side-nav-item flex items-center gap-3 px-3 py-2 rounded-md text-sm
          transition-interactive relative group
          ${
            isActive
              ? "text-white font-semibold bg-[var(--surface-glass)]"
              : "text-[var(--text-secondary)] hover:text-white hover:bg-white/5"
          }
        `}
        aria-current={isActive ? "page" : undefined}
        data-nav-item-id={id}
        title={isCollapsed ? label : undefined}
      >
        {/* Active indicator — left border accent */}
        {isActive && (
          <span
            className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-[var(--brand-primary)]"
            aria-hidden="true"
          />
        )}

        <Icon size={18} className="shrink-0" aria-hidden="true" />

        {!isCollapsed && (
          <>
            <span className="side-nav-label flex-1 truncate">{label}</span>
            {badgeCount !== undefined && badgeCount > 0 && (
              <NavBadge count={badgeCount} />
            )}
          </>
        )}
      </a>
    </li>
  );
}
